import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/common/SEO';
import { useHydrated } from '../hooks/useHydrated';
import { useTranslation } from '../hooks/useTranslation';
import { zodiacSigns } from '../data/zodiacSigns';
import './Horoscope.css';

const Horoscope = () => {
  const hydrated = useHydrated();
  const { t } = useTranslation();
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.05 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.3 }
    }
  };

  return (
    <div className="horoscope-page">
      <SEO
        title="Daily Horoscope - All 12 Zodiac Signs"
        description="Read today's horoscope for all 12 zodiac signs. Get daily, weekly, and monthly predictions for Aries, Taurus, Gemini, Cancer, Leo, Virgo, Libra, Scorpio, Sagittarius, Capricorn, Aquarius, and Pisces."
        keywords="daily horoscope, zodiac predictions, horoscope today, aries horoscope, taurus horoscope, gemini horoscope, leo horoscope, virgo horoscope"
        canonicalPath="/horoscope"
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Daily Horoscope for All Zodiac Signs",
          "description": "Select your zodiac sign to discover what the stars have in store for you today"
        }, {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Should I follow my Sun sign or Moon sign horoscope?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "In Vedic astrology, the Moon sign horoscope is more relevant for daily life predictions as it reflects your mind and emotional state. The Sun sign relates to your soul's purpose and is more relevant over the long term. For the most accurate insights, read your Moon sign horoscope. If you don't know your Moon sign, use our free Kundli generator to find it."
              }
            },
            {
              "@type": "Question",
              "name": "What is the difference between daily, weekly, and monthly horoscopes?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Daily horoscopes reflect fast-moving planetary transits (Moon, Sun, Mercury, Venus, Mars) and are best for immediate guidance on decisions, mood, and interactions. Weekly horoscopes capture medium-term planetary themes. Monthly horoscopes show slower planetary movements (Jupiter, Saturn, Rahu-Ketu) and reveal larger life trends. Use all three for a complete picture."
              }
            },
            {
              "@type": "Question",
              "name": "Which zodiac signs are most compatible?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "In Vedic astrology, compatibility is primarily determined by Nakshatra matching and the relationship between Moon signs. Generally, signs of the same element (Fire-Fire, Earth-Earth, Air-Air, Water-Water) share natural harmony. Signs in a trine relationship (1-5-9) also have excellent compatibility. However, for marriage, a detailed Kundli matching (12 Porutham or Ashtakoota) provides far more accurate compatibility analysis."
              }
            },
            {
              "@type": "Question",
              "name": "How often should I check my horoscope?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Daily horoscope readings help you navigate each day with cosmic awareness. It is most beneficial to read your horoscope in the morning to set your intentions and prepare for the day's energies. During significant planetary transits like Sade Sati, Guru Peyarchi, or eclipses, checking your weekly and monthly horoscope provides important guidance for that period."
              }
            }
          ]
        }]}
      />
      <div className="container">
        <motion.div
          className="page-header"
          initial={hydrated ? { opacity: 0, y: -20 } : false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1>{t('horoscope.title')}</h1>
          <p>{t('horoscope.subtitle')}</p>
        </motion.div>

        <motion.div
          className="horoscope-grid"
          variants={containerVariants}
          initial={hydrated ? "hidden" : false}
          animate="visible"
        >
          {zodiacSigns.map((sign) => (
            <motion.div key={sign.id} variants={itemVariants}>
              <Link
                to={`/horoscope/${sign.name.toLowerCase()}`}
                className="horoscope-card"
                style={{ '--sign-color': sign.color }}
              >
                <div className="horoscope-card-bg"></div>
                <div className="horoscope-card-content">
                  <div className="sign-symbol">{sign.symbol}</div>
                  <h3>{sign.name} <span style={{ fontSize: '0.7em', color: '#ff9800', opacity: 0.85 }}>({sign.tamilNameEn})</span></h3>
                  <span className="sign-dates">{sign.dates}</span>
                  <span className="sign-element">{sign.element} Sign</span>
                  <div className="card-hover-content">
                    <p>{sign.traits.slice(0, 3).join(' • ')}</p>
                    <span className="read-more">{t('horoscope.readHoroscope')}</span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>

        <div className="horoscope-info">
          <div className="info-card">
            <h3>Understanding Your Horoscope</h3>
            <p>
              Your daily horoscope is calculated based on the position of celestial bodies
              at the time of your birth. Our expert astrologers analyze planetary transits,
              aspects, and house positions to provide you with accurate predictions.
            </p>
            <div className="info-features">
              <div className="info-feature">
                <span className="feature-icon">🌟</span>
                <span>Daily Updates</span>
              </div>
              <div className="info-feature">
                <span className="feature-icon">🎯</span>
                <span>Accurate Predictions</span>
              </div>
              <div className="info-feature">
                <span className="feature-icon">💫</span>
                <span>Expert Analysis</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom SEO Content */}
        <div className="page-bottom-content">
          <h2>Daily Horoscope Predictions for All 12 Zodiac Signs</h2>
          <p>Read your daily horoscope predictions based on Vedic astrology for all 12 zodiac signs — Aries, Taurus, Gemini, Cancer, Leo, Virgo, Libra, Scorpio, Sagittarius, Capricorn, Aquarius, and Pisces. Our horoscopes are calculated using real-time planetary transits and their aspects on your Moon sign.</p>
          <p>Each horoscope covers key areas of your life including career, relationships, health, and finances. Planetary movements such as Saturn transit (Sade Sati), Jupiter transit, and Rahu-Ketu axis changes significantly influence your zodiac predictions. Check your daily, weekly, and monthly horoscope to stay aligned with cosmic energies and make informed decisions.</p>

          <h3>How Vedic Horoscopes Differ from Western Astrology</h3>
          <p>Vedic astrology (Jyotish) uses the sidereal zodiac, which accounts for the Earth's axial precession — a phenomenon called the Ayanamsa. This makes Vedic zodiac signs approximately 23 degrees behind Western tropical zodiac signs. Most importantly, Vedic horoscopes are based on your Moon sign (Chandra Rashi) rather than your Sun sign, because the Moon governs the mind, emotions, and daily experiences. This is why your Vedic horoscope may feel more personally accurate than a standard Western Sun-sign reading.</p>

          <h3>Major Planetary Transits That Affect Your Horoscope</h3>
          <p><strong>Saturn Transit (Sade Sati):</strong> Sade Sati is a 7.5-year period when Saturn transits through the sign before, the same sign as, and the sign after your natal Moon sign. It is considered a time of challenges, hard work, and karmic lessons — but also significant growth and transformation for those who persevere. Saturn's transit through each sign lasts approximately 2.5 years.</p>
          <p><strong>Jupiter Transit (Guru Peyarchi):</strong> Jupiter, the planet of wisdom, fortune, and expansion, transits each zodiac sign approximately once per year. A favorable Jupiter transit can bring career growth, marriage, children, wealth, and spiritual advancement. Jupiter is the greatest benefic in Vedic astrology and its transit has a significant positive influence on your Moon sign and the houses it aspects.</p>
          <p><strong>Rahu-Ketu Axis:</strong> Rahu and Ketu (the lunar nodes) transit the zodiac in an 18-month cycle, moving in retrograde motion. They create eclipses and trigger sudden, unexpected changes in life. The house axis Rahu-Ketu occupy in your birth chart reveals the karmic direction of your current life journey.</p>

          <h3>Frequently Asked Questions About Horoscope Predictions</h3>
          <div className="faq-seo">
            <div className="faq-seo-item">
              <h4>Should I follow my Sun sign or Moon sign horoscope?</h4>
              <p>In Vedic astrology, the Moon sign horoscope is more relevant for daily life predictions as it reflects your mind and emotional state. The Sun sign relates to your soul's purpose and is more relevant over the long term. For the most accurate insights, read your Moon sign horoscope. If you don't know your Moon sign, use our free Kundli generator to find it.</p>
            </div>
            <div className="faq-seo-item">
              <h4>What is the difference between daily, weekly, and monthly horoscopes?</h4>
              <p>Daily horoscopes reflect fast-moving planetary transits (Moon, Sun, Mercury, Venus, Mars) and are best for immediate guidance on decisions, mood, and interactions. Weekly horoscopes capture medium-term planetary themes. Monthly horoscopes show slower planetary movements (Jupiter, Saturn, Rahu-Ketu) and reveal larger life trends. Use all three for a complete picture.</p>
            </div>
            <div className="faq-seo-item">
              <h4>Which zodiac signs are most compatible?</h4>
              <p>In Vedic astrology, compatibility is primarily determined by Nakshatra matching and the relationship between Moon signs. Generally, signs of the same element (Fire-Fire, Earth-Earth, Air-Air, Water-Water) share natural harmony. Signs in a trine relationship (1-5-9) also have excellent compatibility. However, for marriage, a detailed Kundli matching (12 Porutham or Ashtakoota) provides far more accurate compatibility analysis.</p>
            </div>
            <div className="faq-seo-item">
              <h4>How often should I check my horoscope?</h4>
              <p>Daily horoscope readings help you navigate each day with cosmic awareness. It is most beneficial to read your horoscope in the morning to set your intentions and prepare for the day's energies. During significant planetary transits like Sade Sati, Guru Peyarchi, or eclipses, checking your weekly and monthly horoscope provides important guidance for that period.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Horoscope;
